import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { apiRequest } from '../../api/client';
import { canLiveVerifyHere } from '../../lib/capabilities';
import { REVISION_SOURCE_COLOR } from '../../lib/docs';

/**
 * DOCUMENT HISTORY — every change made to a document, across every doc and language.
 *
 * This is the V0.20 Logs screen, moved verbatim from `features/logs/LogsPanel.tsx` into the
 * dashboard's Logs tab (V0.49). Every id the regression suite drives is unchanged —
 * `#logs-table`, `#logs-tbody`, `#logs-search`, `#logs-source`, `#logs-locale`, `#logs-issues`,
 * `#logs-count`, `#logs-more`, `#logs-to-generate`, `#logs-to-targets` — and so is every
 * behaviour below. What went: the panel's own `.perm-head`, because the dashboard has one, and
 * the `open` state, the handle and the `reportAdminScreen` effect, because the tab owns all
 * three now. The two cross-links moved into the controls row rather than being deleted.
 *
 * IT IS NOT FOLDED INTO THE ACTIONS TABLE, and that was a decision rather than an omission. A
 * document version is a thing you can READ and RESTORE — it carries a content snapshot, a
 * coverage report and a prompt — while an audit row is an event with no body. The Actions view
 * records that a page was saved and links to the version; this is where the version lives.
 *
 * SERVER-SIDE vs CLIENT-SIDE FILTERING, unchanged and still worth stating: search, kind and
 * language go to the server and reset paging. "Only entries with issues" and the sort do not —
 * they apply to the rows already loaded, because the issue flag spans two JSON columns the
 * query cannot index. That is honest only because the count says what it is showing, which is
 * why it reads "N shown · M total" rather than a single number.
 */

interface Coverage {
  status: string;
  instruction: string;
  note?: string;
}

interface Revision {
  id: string;
  createdAt: string;
  title?: string;
  nodeLabel?: string;
  locale?: string;
  sourceLocale?: string;
  sourceVersion?: number;
  version: number;
  label?: string;
  actorLabel?: string;
  source: string;
  status?: string;
  imagesDropped?: number;
  coverage?: Coverage[];
  issues?: { kind: string; detail: string }[];
}

/** The kinds of change a revision can be. Server values, so not translated. */
const SOURCES: [string, string][] = [
  ['', 'All kinds'],
  ['ai_generated', 'AI generated'],
  ['ai_translated', 'AI translated'],
  ['human_edit', 'Human edit'],
  ['restore', 'Restore'],
  ['ai_failed', 'Failed'],
];

type SortKey = 'createdAt' | 'version';

/** Everything that is wrong with a revision, as one readable line. */
function signalsFor(r: Revision): string[] {
  const out: string[] = [];
  if (r.status === 'error') out.push('failed');
  if ((r.imagesDropped ?? 0) > 0) {
    out.push(`${r.imagesDropped} invented image${r.imagesDropped === 1 ? '' : 's'}`);
  }
  const partial = (r.coverage || []).filter((c) => c.status !== 'done').length;
  if (partial) out.push(`${partial} not fully done`);
  if ((r.issues || []).length) {
    out.push(`${r.issues!.length} issue${r.issues!.length === 1 ? '' : 's'}`);
  }
  return out;
}

const hasIssues = (r: Revision): boolean =>
  (r.issues || []).length > 0 ||
  (r.coverage || []).some((c) => c.status !== 'done') ||
  (r.imagesDropped ?? 0) > 0 ||
  r.status === 'error';

export function DocHistoryView({ onLeave }: { onLeave: () => void }) {
  const s = useAppState();

  const [search, setSearch] = useState(''); // what is typed
  const [query, setQuery] = useState(''); // what has been sent, 300ms behind
  const [source, setSource] = useState('');
  const [locale, setLocale] = useState('');
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [sort, setSort] = useState<SortKey>('createdAt');
  const [asc, setAsc] = useState(false);

  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<Revision[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Typing is debounced into `query`; `query` is what the request is keyed on. Splitting the
  // two is what stops every keystroke being a round trip without the input lagging behind
  // what was typed.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setQuery(search);
      setPage(0);
    }, 300);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [search]);

  // One fetch per (filters, page, language) combination. Page 0 replaces the rows; any later
  // page appends, which is what makes "Load more" additive rather than a jump.
  //
  // `s.currentLocale` is in here because a language switch re-reads everything that is on
  // screen — titles and node labels are per-language, so a view left open across a switch
  // would go on showing the previous language's text.
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);

    const q = new URLSearchParams();
    if (query) q.set('search', query);
    if (source) q.set('source', source);
    if (locale) q.set('locale', locale);
    q.set('page', String(page));

    void apiRequest<{ total: number; revisions: Revision[] }>(
      `/api/docs/revisions/all?${q.toString()}`,
    ).then(
      (d) => {
        if (!live) return;
        setTotal(d.total);
        setRows((prev) => (page === 0 ? d.revisions : prev.concat(d.revisions)));
        setLoading(false);
      },
      (e: Error) => {
        if (!live) return;
        setError(e.message);
        setLoading(false);
      },
    );
    return () => {
      live = false;
    };
  }, [query, source, locale, page, s.currentLocale]);

  const shown = useMemo(() => {
    const filtered = issuesOnly ? rows.filter(hasIssues) : rows;
    const dir = asc ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = sort === 'version' ? a.version : new Date(a.createdAt).getTime();
      const bv = sort === 'version' ? b.version : new Date(b.createdAt).getTime();
      return av < bv ? -dir : av > bv ? dir : 0;
    });
  }, [rows, issuesOnly, sort, asc]);

  const toggleSort = (key: SortKey) => {
    if (sort === key) setAsc((a) => !a);
    else {
      setSort(key);
      setAsc(false);
    }
  };

  const sortClass = (key: SortKey) =>
    `logs-sortable${sort === key ? ' on' : ''}${sort === key && asc ? ' asc' : ''}`;

  return (
    <div id="dash-logs-history">
      <div className="perm-section-lead">
        Every change made to a document — what was asked of the agent, what it managed, and what
        got in its way.
      </div>

      <div className="logs-controls">
        <input
          className="fld"
          id="logs-search"
          placeholder="Search titles, prompts, change notes, people…"
          style={{ flex: 1, minWidth: 220 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="fld"
          id="logs-source"
          style={{ width: 'auto' }}
          value={source}
          onChange={(e) => {
            setSource(e.target.value);
            setPage(0);
          }}
        >
          {SOURCES.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <select
          className="fld"
          id="logs-locale"
          style={{ width: 'auto' }}
          value={locale}
          onChange={(e) => {
            setLocale(e.target.value);
            setPage(0);
          }}
        >
          <option value="">All languages</option>
          {s.languages.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name}
            </option>
          ))}
        </select>
        <label className="filterrow" style={{ padding: '0 8px' }}>
          <input
            type="checkbox"
            id="logs-issues"
            checked={issuesOnly}
            onChange={(e) => setIssuesOnly(e.target.checked)}
          />
          <span>Only entries with issues</span>
        </label>
        {/* Two numbers, because they mean different things: the issues filter and the
            paging both apply to what is loaded, not to what exists. */}
        <span id="logs-count" className="tgt-meta">
          {shown.length} shown · {total} total
        </span>

        {/* The two cross-links the panel's header used to carry. They kept their ids: the
            generation console is a sibling tab now, so reaching it is a tab switch, while
            Targets is a separate admin screen and still closes this one. */}
        <button
          className="btn"
          id="logs-to-generate"
          onClick={() => callAction('openAiDocModal')}
        >
          <svg className="ico">
            <use href="#i-ai" />
          </svg>
          <span>Generate documentation</span>
        </button>

        {/* Targets belong to the live-verification capability, not to AI generally: an
            offline build ships without the browser the agent drives, so the way there is
            removed rather than left to fail. */}
        {canLiveVerifyHere(s.perm, s.caps) ? (
          <button
            className="btn"
            id="logs-to-targets"
            onClick={() => {
              onLeave();
              callAction('openTargets');
            }}
          >
            <svg className="ico">
              <use href="#i-server" />
            </svg>
            <span>Targets</span>
          </button>
        ) : null}
      </div>

      <div className="card">
        <table className="utable" id="logs-table">
          <thead>
            <tr>
              <th className={sortClass('createdAt')} onClick={() => toggleSort('createdAt')}>
                Date
              </th>
              <th>Document</th>
              <th>Node</th>
              <th>Lang</th>
              <th className={sortClass('version')} onClick={() => toggleSort('version')}>
                Version
              </th>
              <th>Change</th>
              <th>By</th>
              <th>Signals</th>
            </tr>
          </thead>
          <tbody id="logs-tbody">
            {error ? (
              <tr>
                <td colSpan={8} style={{ color: 'var(--ink-faint)' }}>
                  Could not load logs ({error}).
                </td>
              </tr>
            ) : loading && !rows.length ? (
              <tr>
                <td colSpan={8} style={{ color: 'var(--ink-faint)' }}>
                  Loading…
                </td>
              </tr>
            ) : !shown.length ? (
              <tr>
                <td colSpan={8} style={{ color: 'var(--ink-faint)' }}>
                  No matching entries.
                </td>
              </tr>
            ) : (
              shown.map((r) => {
                const d = new Date(r.createdAt);
                const signals = signalsFor(r);
                return (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {d.toLocaleDateString()}{' '}
                      <span style={{ color: 'var(--ink-faint)' }}>
                        {d.toLocaleTimeString().slice(0, 5)}
                      </span>
                    </td>
                    {/* Long titles and change notes clamp to a readable width — the full
                        text is one click away in the detail drawer. The clamp sits on the
                        Change cell's BUTTON rather than its td: a td's text-overflow
                        cannot ellipsize an inline-block child's own text, only clip it. */}
                    <td className="logs-cell-clamp">
                      <span
                        className="logrow-src"
                        style={{ background: REVISION_SOURCE_COLOR[r.source] || 'var(--ink-faint)' }}
                      />
                      {r.title || ''}
                    </td>
                    <td>{r.nodeLabel || '—'}</td>
                    <td>
                      {(r.locale || '').toUpperCase()}
                      {r.sourceLocale ? (
                        <span style={{ color: 'var(--ink-faint)' }}>
                          {' '}
                          ←{r.sourceLocale.toUpperCase()}V{r.sourceVersion ?? '?'}
                        </span>
                      ) : null}
                    </td>
                    <td>V{r.version}</td>
                    <td>
                      <button
                        className="logs-link logs-cell-clamp"
                        onClick={() => callAction('openLogDetail', r.id)}
                      >
                        {r.label || '(no description)'}
                      </button>
                    </td>
                    <td>{r.actorLabel || ''}</td>
                    <td
                      style={{
                        color: signals.length
                          ? 'var(--status-needs-update,#e0762d)'
                          : 'var(--ink-faint)',
                      }}
                    >
                      {signals.join(' · ') || '—'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div id="logs-more" style={{ marginTop: 14 }}>
        {rows.length < total ? (
          <button className="pbtn" disabled={loading} onClick={() => setPage((p) => p + 1)}>
            Load more
          </button>
        ) : null}
      </div>
    </div>
  );
}
